import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  unsafeCSS,
  css,
  TemplateResult,
} from '@design.estate/dees-element';

@customElement('philkunz-content')
export class PhilkunzContent extends DeesElement {
  @property()
  public someProperty = 'someProperty';

  constructor() {
    super();
  }

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        position: relative;
        display: block;
        min-height: 120px;
        font-family: 'Geist Sans';
        color: ${cssManager.bdTheme('#333', '#ccc')};
        background: ${cssManager.bdTheme('#eeeeeb', '#000000')};
        box-shadow: 0px -2px 10px ${cssManager.bdTheme('#eeeeebff', 'rgba(0, 0, 0, 1)')};
        border-top: 1px solid;
        border-image: radial-gradient(circle, #88888840, #88888800) 1;
        z-index: 2; /* Increased z-index so its shadow isn't hidden */
      }
      :host([hidden]) {
        display: none;
      }

      .grid {
        position: relative;
        max-width: 1200px;
        margin: auto;
        display: grid;
        grid-template-columns: calc(100% / 3 - 12px) calc(100% / 3 + 24px) calc(100% / 3 - 12px);
      }

      ${cssManager.cssForNotebook(css`
        .grid {
          grid-template-columns: ${cssManager.cssGridColumns(2, 0)};
        }
      `)}

      ${cssManager.cssForPhablet(css`
        .grid {
          grid-template-columns: ${cssManager.cssGridColumns(1, 0)};
        }
      `)}

      .griditem {
        padding: 32px;
      }

      .griditem:nth-child(3n + 1) {
        padding-left: 0px;
        border-right: 1px solid;
        border-image: radial-gradient(circle, #88888880, #88888800) 1;
      }

      .griditem:nth-child(3n + 2) {
        border-right: 1px solid;
        border-image: radial-gradient(circle, #88888880, #88888800) 1;
      }

      .griditem:nth-child(3n + 3) {
        border-right: none;
        padding-right: 0px;
      }

      ${cssManager.cssForNotebook(css`
        .griditem:nth-child(n) {
          padding-left: 24px;
        }
      `)}

      ${cssManager.cssForPhablet(css`
        .griditem:nth-child(n) {
          padding-left: 24px;
          padding-right: 24px;
        }
      `)}

      

      h1,
      h2 {
        font-family: 'Cal Sans';
        letter-spacing: 0.025em;
        text-align: left;
        font-size: 24px;
        font-weight: bold;
        margin: 16px 0px 16px 0px;
      }

      h2 {
        font-size: 16px;
        font-weight: bold;
        margin: 0px 0px 0px 0px;
      }

      h2 dees-icon {
        display: inline-block;
        transform: translateY(2px);
        padding-right: 8px;
        color: #e4002b;
      }

      .separator {
        height: 8px;
        width: 16px;
        background: #e4002b;
        border-radius: 8px;
        margin: 16px 0px 16px 0px;
      }

      p {
        max-width: 600px;
        margin: 16px 0px 16px 0px;
        font-size: 16px;
        font-weight: 400;
        font-family: 'Geist Sans';
        line-height: 1.5em;
      }

      .imgcontainer {
        position: relative;
        width: 100%;
        border-radius: 3px;
        overflow: hidden;
        margin: 16px 0px 16px 0px;
        box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
      }

      img {
        display: block;
        width: 100%;
        transform: scale(165%);
      }

      a {
        display: inline-block;
        color: ${cssManager.bdTheme('#333', '#fff')};
        will-change: transform;
        transform: scale(1);
        transition: transform 0.2s ease-in-out;
      }

      a:hover {
        transform: scale(1.02);
      }

      .linkbox {
        display: block;
        background: ${cssManager.bdTheme('#ffffff', '#ffffff10')};
        border-top: 1px solid ${cssManager.bdTheme('#00000000', '#ffffff20')};
        padding: 16px 16px;
        border-radius: 8px;
        margin-bottom: 16px;
        transition: transform 0.2s;
        text-decoration: none;
      }

      .linkbox:hover {
        transform: scale(1.02, 1.02);
        cursor: pointer;
      }

      .linkbox h2 {
        font-family: 'Dees Sans';
        font-weight: 400;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <style></style>
      <div class="grid">
        <div class="griditem">
          <h1>Who am I?</h1>
          <div class="separator"></div>
          <p>
            Hi, I'm Phil, a European entrepreneur focusing on building solutions that simplify life.
            :)
          </p>
          <p>
            I love Jazz, Coffee and Tech. Long streched breakfasts with Miles Davis playing in the
            background while coding on ideas on a Macbook are kind of my thing.
          </p>

          <p>
            I tend to have a mind of my own, but I'm humble enough to know: I'm often not the
            smartest person in the room. I yearn to see what others are thinking, what ideas they
            have. Having said that: Dreaming together is one of the nicest things there is. You
            don't have to fulfill every dream, but without a dream, you don't know what to actually
            do. Talk to me. I'd love to get to know your dreams.
          </p>
        </div>
        <div class="griditem">
          <h1>Consulting:</h1>
          <div class="separator"></div>
          <p>
            I'm a reasonably opininiated person. Sometimes I am wrong in my opinions and assertions and I
            stand corrected. I don't have a problem with that. One needs to be able to correct
            oneselves through learning every day.
          </p>
          <p>
            I can follow decisions, and I can make decisions. Most importantly, I'll get the data to
            make a decision. And I'll revert it, if the data tells me to do so. With this
            skillset, combined with my technical background and team player mentality, I can lead projects to success, no matter the position I'm being assigned.
          </p>
          <p>Feel free to reach out to me.</p>
          <dees-button
            @click=${async () => {
              await this.domtoolsPromise;
              this.domtools.keyboard.triggerKeyPress([
                this.domtools.keyboard.keyEnum.Ctrl,
                this.domtools.keyboard.keyEnum.S,
              ]);
            }}
            >Let's Chat -></dees-button
          >
        </div>
        <div class="griditem">
          <h1>Me across the web:</h1>
          <div class="separator"></div>
          <a class="linkbox" href="https://code.foss.global/philkunz?tab=activity" target="_blank">
            <h2><dees-icon iconFA="arrowRight"></dees-icon>My Commits on foss.global</h2>
          </a>
          <a class="linkbox" href="https://typescript.guru/author/philipp" target="_blank">
            <h2><dees-icon iconFA="arrowRight"></dees-icon>My Articles on TypeScript.Guru</h2>
          </a>
          <a class="linkbox" href="https://coffee.link/author/phil/" target="_blank">
            <h2><dees-icon iconFA="arrowRight"></dees-icon>My Articles on coffee.link</h2>
          </a>
          <a class="linkbox" href="https://headon.consulting/team" target="_blank">
            <h2><dees-icon iconFA="arrowRight"></dees-icon>My Team on headon.consulting</h2>
          </a>
          <a
            class="linkbox"
            href="https://www.freelancermap.de/profil/philipp-kunz"
            target="_blank"
          >
            <h2><dees-icon iconFA="arrowRight"></dees-icon>My Profile on freelancermap.com</h2>
          </a>
        </div>
      </div>
    `;
  }
}
